import { useQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';

import { makeAPIRequestReactQueryWithHeaders } from '../api';
import { getAccessToken } from '@/utils/api';

import { endpoints } from '../endpoints';

import { usePreferences } from '../usePreferences';

export type FieldType =
  | 'text'
  | 'select'
  | 'number'
  | 'range'
  | 'boolean';

export interface FieldConstraints {
  suggestedValues?: string[];
  min?: number;
  max?: number;
  pattern?: string;
}

export interface FieldDefinition {
  key: string;
  label: string;
  type: FieldType;
  description?: string;
  instruction?: string;
  unit?: string;
  required: boolean;
  defaultValue?: string | number | boolean;
  constraints?: FieldConstraints;
  categories?: string[];
}

export interface GetVoiceNoteFieldsResponse {
  fields: FieldDefinition[];
}

/**
 * Fetches effective field definitions for the current practice (one per field key),
 * with practice overrides applied over org and global. Use for catalog / customizer UI.
 * Requires Authorization, practice, and org headers.
 */
export const useVoiceNoteFields = () => {
  const { data: preferences } = usePreferences();
  const isVideaNotesEnabled = preferences?.isVideaNotesEnabled;

  const {
    primaryPracticeId: practiceId,
    primaryOrganizationId: orgId,
  } = useSelector(
    (state: {
      login: {
        user: {
          primaryPracticeId?: string;
          primaryOrganizationId?: string;
        };
      };
    }) => state.login.user,
  );

  return useQuery({
    queryKey: [
      'voice-note-fields',
      practiceId,
      orgId,
    ],
    queryFn: async () => {
      const token = getAccessToken();
      return makeAPIRequestReactQueryWithHeaders<GetVoiceNoteFieldsResponse>(
        `/${endpoints.ambient.voiceNotes.voiceNoteFields}`,
        {},
        {
          practice: practiceId,
          org: orgId,
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        'GET',
      );
    },
    enabled:
      practiceId !== undefined &&
      orgId !== undefined &&
      isVideaNotesEnabled === true,
  });
};
